import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import useTranslate from '@/hooks/use-translate'
import { CommandBarFooter } from './CommandBarFooter'
import { CommandBarResults } from './CommandBarResults'
import { CommandBarSearchInput } from './CommandBarSearchInput'
import {
  commandBarBackdropSx,
  commandBarPanelSx,
} from './commandBar.styles'
import { useCommandBarExecute } from './hooks/useCommandBarExecute'
import { useCommandBarOverlayKeyboard } from './hooks/useCommandBarOverlayKeyboard'
import { useCommandBarRecents } from './hooks/useCommandBarRecents'
import { useCommandBarSearch } from './hooks/useCommandBarSearch'
import { useCommandBarSections } from './hooks/useCommandBarSections'
import type { CommandBarModalId } from './types'

type CommandBarOverlayProps = {
  open: boolean
  onClose: () => void
  openModal: (modalId: CommandBarModalId) => void
}

export const CommandBarOverlay = ({
  open,
  onClose,
  openModal,
}: CommandBarOverlayProps) => {
  const { t } = useTranslate('commandBar')
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const { recents, addRecent } = useCommandBarRecents()
  const { isSearching, hasQuery, entityResults, showReferentials } =
    useCommandBarSearch(query)
  const { sections, flatItems } = useCommandBarSections({
    query,
    hasQuery,
    entityResults,
    recents,
    showReferentials,
  })
  const { executeItem, executeAction, canExecuteAction } = useCommandBarExecute(
    {
      close: onClose,
      openModal,
      addRecent,
    }
  )

  useEffect(() => {
    if (!open) return
    setQuery('')
    setSelectedIndex(0)
  }, [open])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query, sections.length])

  const executeSelected = useCallback(() => {
    const item = flatItems[selectedIndex]
    if (item) executeItem(item)
  }, [executeItem, flatItems, selectedIndex])

  const moveSelection = useCallback(
    (direction: 'up' | 'down') => {
      setSelectedIndex((prev) => {
        if (flatItems.length === 0) return prev
        if (direction === 'up') {
          return prev <= 0 ? flatItems.length - 1 : prev - 1
        }
        return prev >= flatItems.length - 1 ? 0 : prev + 1
      })
    },
    [flatItems.length]
  )

  useCommandBarOverlayKeyboard({
    open,
    flatItems,
    onClose,
    onMoveSelection: moveSelection,
    onExecuteSelected: executeSelected,
    onExecuteAction: executeAction,
    canExecuteAction,
  })

  if (!open) return null

  return (
    <Box
      sx={commandBarBackdropSx}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <Box
        sx={commandBarPanelSx}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <CommandBarSearchInput
          value={query}
          placeholder={t('searchPlaceholder')}
          onChange={setQuery}
          onClose={onClose}
        />
        <CommandBarResults
          sections={sections}
          selectedIndex={selectedIndex}
          isSearching={isSearching}
          hasQuery={hasQuery}
          onHoverItem={setSelectedIndex}
          onExecuteItem={(index) => {
            const item = flatItems[index]
            if (item) executeItem(item)
          }}
        />
        <CommandBarFooter />
      </Box>
    </Box>
  )
}
